import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Skeleton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';

import SftGlobalData from '../../library/SftGlobalData';
import ReportResultGrid from './ReportResultGrid';
import SizeMatrixReportGrid, { isSizeMatrix } from './SizeMatrixReportGrid';
import ReportLayoutActions from './ReportLayoutActions';
import ReportPdfView from './ReportPdfView';
import { DateQuickPicks, MultiSelectField, RecIdInput } from './ReportVariableInputs';
import { recIdLookupKind } from './recIdLabels';
import { splitRecIdList, validateRecIds } from './reportPipeline';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const localizedTitle = (report, lang) => {
    if (!report) return '';
    switch (lang) {
        case 'nl': return report.reptitle  || report.reptitle2 || report.reptitle3 || report.reptitle4 || '';
        case 'fr': return report.reptitle2 || report.reptitle  || report.reptitle3 || report.reptitle4 || '';
        case 'en': return report.reptitle3 || report.reptitle  || report.reptitle2 || report.reptitle4 || '';
        default:   return report.reptitle  || '';
    }
};

// The caption MyFashionManager shows (server `caption` {nl, fr, en}:
// report_var texts for inputs, the selection tab name for pick lists),
// never the raw field name such as voorraad.levnr.
const localizedVarLabel = (v, lang) => {
    const caption = v.caption && typeof v.caption === 'object' ? v.caption[lang] || v.caption.nl : '';
    if (caption) return caption;
    switch (lang) {
        case 'nl': return v.repvar       || v.repvarlang2 || v.repvarlang3 || v.repvar;
        case 'fr': return v.repvarlang2  || v.repvar      || v.repvarlang3 || v.repvar;
        case 'en': return v.repvarlang3  || v.repvar      || v.repvarlang2 || v.repvar;
        default:   return v.repvar;
    }
};

const localizedCodeLabel = (option, lang) => {
    switch (lang) {
        case 'nl': return option.label;
        case 'fr': return option.codeoms_lang2 || option.kleuroms_lang2 || option.label;
        case 'en': return option.codeoms_lang3 || option.kleuroms_lang3 || option.label;
        default:   return option.label;
    }
};

const initialValueFor = (v) => {
    if (v.kind === 'date') {
        const def = (v.repdefvalue || '').toLowerCase();
        if (def === 'now()' || def === 'now') return dayjs();
        if (def) {
            // A fixed default is written the Belgian way (dd/mm/yyyy, also
            // with - or .); dayjs alone would read 01/02/2026 as 2 January.
            const be = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(def.trim());
            const parsed = be ? dayjs(new Date(Number(be[3]), Number(be[2]) - 1, Number(be[1]))) : dayjs(def);
            return parsed.isValid() ? parsed : null;
        }
        return null;
    }
    if (v.kind === 'number') {
        return v.repdefvalue ?? '';
    }
    if (v.kind === 'multi') {
        return [];
    }
    return v.repdefvalue ?? '';
};

const serialiseForBackend = (v, raw) => {
    if (v.kind === 'date') {
        if (!raw) return '';
        if (raw && typeof raw === 'object' && raw.format) {
            return raw.format('YYYY-MM-DD');
        }
        return String(raw);
    }
    if (v.kind === 'multi') {
        if (!Array.isArray(raw)) return [];
        return raw.map(o => (typeof o === 'object' && o !== null ? o.value : o));
    }
    return raw ?? '';
};

// Last used inputs per report (variables, document ids, store/register),
// restored the next time the report is opened. Browser storage may be
// unavailable (private mode) - every access is guarded.
const inputsKey = (repid) => 'sft_report_inputs_' + repid; // "Mijn lijsten": 'my_' + repid
const loadSavedInputs = (repid) => {
    try {
        const raw = window.localStorage.getItem(inputsKey(repid));
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};
const saveInputs = (repid, data) => {
    try {
        window.localStorage.setItem(inputsKey(repid), JSON.stringify(data));
    } catch (e) {
        // storage unavailable - inputs simply are not remembered
    }
};

/**
 * MyFashionManager's filter rule (PrintDialogUnit.pas MustSelectAFilter,
 * :4611-4955), checked before a run like MFM does on "Print":
 *  - a filter with rep_must_use needs a value -> { code: 'specific', variable };
 *  - report.must_select_filter without required filters: at least one of
 *    the filters (selection lists and text inputs; dates/numbers never
 *    count) needs a value -> { code: 'any' }. Not enforced when the report
 *    has no such filter at all (it could never run).
 * null = fine. auth (reports.php checkRequiredFilters) applies the same rule.
 */
export const requiredFilterProblem = (report, variables, values) => {
    const vars = Array.isArray(variables) ? variables : [];
    const filled = (v) => {
        const raw = values ? values[v.repvar] : undefined;
        if (v.kind === 'multi') return Array.isArray(raw) && raw.length > 0;
        if (v.kind === 'date') return !!raw && (typeof raw !== 'object' || !raw.isValid || raw.isValid());
        return raw !== undefined && raw !== null && String(raw).trim() !== '';
    };
    for (const v of vars) {
        if (v.rep_must_use && !filled(v)) return { code: 'specific', variable: v };
    }
    const hasRequired = vars.some(v => v.rep_must_use);
    const candidates = vars.filter(v => v.kind === 'multi' || v.kind === 'text');
    if (report && report.must_select_filter && !hasRequired && candidates.length > 0 && !candidates.some(filled)) {
        return { code: 'any' };
    }
    return null;
};

const optionsForVariable = (v, options) => {
    if (!options || !v.option_source) return [];
    if (v.option_source === 'codes' || v.option_source === 'kl_codes' || v.option_source === 'lev_codes') {
        const bucket = options[v.option_source] || {};
        return bucket[v.option_group] || [];
    }
    return options[v.option_source] || [];
};

/**
 * The RepSelection header variable, built like MyFashionManager's
 * VarSelection (PrintDialogUnit.pas :855 AddToVarSelection, :1020-1090,
 * :1194-1199): "Caption:value " per filled input (dates dd/mm/yyyy), and
 * "(desc1;desc2) " per pick list with a selection; duplicates are skipped.
 */
export const buildRepSelection = (variables, rawValues, lang) => {
    let out = '';
    const add = (text) => { if (out.indexOf(text) === -1) out += text; };
    (variables || []).forEach((v) => {
        const raw = rawValues ? rawValues[v.repvar] : undefined;
        if (v.kind === 'multi') {
            const picked = Array.isArray(raw) ? raw : [];
            if (picked.length === 0) return;
            const texts = picked.map(o => (o && typeof o === 'object'
                ? (localizedCodeLabel(o, lang) || String(o.value))
                : String(o)));
            add('(' + texts.join(';') + ') ');
            return;
        }
        if (raw === undefined || raw === null || raw === '') return;
        const caption = localizedVarLabel(v, lang);
        if (v.kind === 'date') {
            const d = raw && typeof raw === 'object' && raw.format ? raw : dayjs(raw);
            if (!d || !d.isValid || !d.isValid()) return;
            add(caption + ':' + d.format('DD/MM/YYYY') + ' ');
            return;
        }
        add(caption + ':' + String(raw) + ' ');
    });
    // RemoveAHT (MainUnit.pas :8067): no apostrophes in report variables.
    return out.replace(/'/g, ' ');
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const ReportRunModal = (props) => {
    const { open, report, onClose } = props;

    // 'vars' | 'running' | 'result' | 'error'
    const [phase, setPhase]               = useState('vars');
    const [loadingVars, setLoadingVars]   = useState(false);
    const [variables, setVariables]       = useState([]);   // server-side definitions
    const [optionLists, setOptionLists]   = useState({});
    const [values, setValues]             = useState({});   // userName → raw value

    const [runId, setRunId]               = useState(null);
    const [statusPayload, setStatusPayload] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    // Document reports: one comma list per {RecId;n} slot (MyFashionManager
    // multi-select), plus the store/register context when the SQL uses
    // {WAREHOUSEID}/{LOCID} or {POSID}.
    const [recIdValues, setRecIdValues]   = useState({});
    const [storeId, setStoreId]           = useState('');
    const [posId, setPosId]               = useState('');
    // Result view: 'pdf' (default for an .fr3 layout, like MyFashionManager)
    // or 'grid'; viewNotice explains a fallback to the grid.
    const [view, setView]                 = useState('grid');
    const [viewNotice, setViewNotice]     = useState('');
    // RepSelection of the last run (the filters, shown in the report header).
    const [repSelection, setRepSelection] = useState('');
    // MyFashionManager's "must select (a) filter" message after Run.
    const [filterError, setFilterError]   = useState('');
    // "Opslaan in mijn lijsten" (SoftTouch users, standard reports only) and
    // the name asked before saving (the report title proposed, like MFM).
    const [saveToMy, setSaveToMy]         = useState(false);
    const [nameDialog, setNameDialog]     = useState(false);
    const [myName, setMyName]             = useState('');
    const isMy = !!(report && report.source === 'my');
    const storeKey = report ? (isMy ? 'my_' + report.repid : report.repid) : '';

    const recIdSlots   = (report && Array.isArray(report.recid_indexes)) ? report.recid_indexes : [];
    const needsContext = !!(report && (recIdSlots.length > 0 || report.uses_store || report.uses_pos));
    const tl = (key, fallback) => {
        const value = props.t ? props.t(key) : null;
        return value && value !== key ? value : fallback;
    };

    const pollTimer = useRef(null);
    const pollAttempts = useRef(0);

    // Keep latest props in a ref so the polling chain (scheduled via setTimeout +
    // useCallback) doesn't capture stale values from the first render.
    const apiRef       = useRef(props.api);
    const tRef         = useRef(props.t);
    const sftUserIdRef = useRef(props.sftUserId);
    useEffect(() => { apiRef.current       = props.api;       }, [props.api]);
    useEffect(() => { tRef.current         = props.t;         }, [props.t]);
    useEffect(() => { sftUserIdRef.current = props.sftUserId; }, [props.sftUserId]);

    // Reset whenever modal closes
    useEffect(() => {
        if (!open) {
            setPhase('vars');
            setLoadingVars(false);
            setVariables([]);
            setOptionLists({});
            setValues({});
            setRunId(null);
            setStatusPayload(null);
            setErrorMessage(null);
            setRecIdValues({});
            setStoreId('');
            setPosId('');
            setView('grid');
            setViewNotice('');
            setRepSelection('');
            setFilterError('');
            setSaveToMy(false);
            setNameDialog(false);
            setMyName('');
            pollAttempts.current = 0;
            if (pollTimer.current) {
                clearTimeout(pollTimer.current);
                pollTimer.current = null;
            }
        }
    }, [open]);

    // Load variables when the modal opens (if the report has any)
    useEffect(() => {
        if (!open || !report) return;

        const saved = loadSavedInputs(storeKey);
        if (saved) {
            if (saved.recids && typeof saved.recids === 'object') setRecIdValues(saved.recids);
            if (saved.storeId) setStoreId(saved.storeId);
            if (saved.posId) setPosId(saved.posId);
        }
        // A firm pick list with one firm: preselect it.
        recIdSlots.forEach((slot) => {
            const kind = recIdLookupKind(report, slot);
            const list = kind && props.lookups && Array.isArray(props.lookups[kind]) ? props.lookups[kind] : [];
            if (kind === 'corps' && list.length === 1 && !(saved && saved.recids && saved.recids[slot])) {
                setRecIdValues((current) => ({ ...current, [slot]: list[0].value }));
            }
        });

        if (!report.has_variables) {
            // Skip straight to execution — unless the report needs a
            // document id or a store/register first.
            if (!needsContext) startExecution({});
            return;
        }

        setLoadingVars(true);
        SftGlobalData.post('reports&action=fetchReportVariables', {
            key: props.api,
            repid: report.repid,
            ...(isMy ? { source: 'my', sft_user_id: sftUserIdRef.current || props.sftUserId || '' } : {}),
        }).then(response => {
            if (typeof response.error === 'undefined' && response?.data) {
                const vars = response.data.variables || [];
                setVariables(vars);
                setOptionLists(response.data.options || {});
                const opts = response.data.options || {};
                const initial = {};
                vars.forEach(v => { initial[v.repvar] = initialValueFor(v); });
                const remembered = (saved && saved.vars) || {};
                vars.forEach(v => {
                    if (!(v.repvar in remembered)) return;
                    const stored = remembered[v.repvar];
                    if (v.kind === 'date') {
                        const parsed = stored ? dayjs(stored) : null;
                        if (parsed && parsed.isValid()) initial[v.repvar] = parsed;
                    } else if (v.kind === 'multi') {
                        const keys = new Set(Array.isArray(stored) ? stored.map(String) : []);
                        initial[v.repvar] = optionsForVariable(v, opts).filter(o => keys.has(String(o.value)));
                    } else if (stored !== undefined && stored !== null) {
                        initial[v.repvar] = stored;
                    }
                });
                setValues(initial);
            } else {
                setErrorMessage((response && response.message) || (props.t('errorOccured') || 'Error'));
                setPhase('error');
            }
            setLoadingVars(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, report?.repid]);

    // ─────────────────────────────────────────────────────────────────────────
    // Execution: start + poll
    // ─────────────────────────────────────────────────────────────────────────

    const startExecution = (varValuesOverride, saveAs) => {
        const rawValues = varValuesOverride ?? values;
        const payload = {};
        variables.forEach(v => {
            payload[v.repvar] = serialiseForBackend(v, rawValues[v.repvar]);
        });

        // A saved list has its filters inside its SQL: its description
        // (the selection when it was saved) is the header's RepSelection.
        const selection = buildRepSelection(variables, rawValues, props.lang)
            || (isMy && report.repdescr ? String(report.repdescr) : '');
        setRepSelection(selection);
        setFilterError('');
        setPhase('running');
        setErrorMessage(null);
        setStatusPayload(null);
        pollAttempts.current = 0;

        const recids = {};
        recIdSlots.forEach(slot => { recids[slot] = splitRecIdList(recIdValues[slot]).join(','); });
        saveInputs(storeKey, { vars: payload, recids, storeId, posId });

        SftGlobalData.post('reports&action=execute', {
            key: props.api,
            sft_user_id: sftUserIdRef.current || props.sftUserId || '',
            repid: report.repid,
            variables: payload,
            recids,
            store_id: storeId,
            pos_id: posId,
            lang: props.lang || '',
            ...(isMy ? { source: 'my' } : {}),
            ...(saveAs ? { save_as: { title: saveAs.title, selection } } : {}),
        }).then(response => {
            if (typeof response.error === 'undefined' && response?.data?.run_id) {
                if (saveAs && response.data.my_report_id) {
                    props.notify && props.notify(tl('reports_my_saved', 'Saved to My lists'), 4000, 'top-center', props.theme, 1);
                    setSaveToMy(false);
                    props.onMyReportSaved && props.onMyReportSaved(response.data.my_report_id);
                }
                setRunId(response.data.run_id);
                schedulePoll(response.data.run_id, 700);
            } else {
                setErrorMessage((response && response.message) || (props.t('errorOccured') || 'Error'));
                setPhase('error');
            }
        });
    };

    const schedulePoll = useCallback((id, delayMs) => {
        if (pollTimer.current) clearTimeout(pollTimer.current);
        pollTimer.current = setTimeout(() => pollStatus(id), delayMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pollStatus = (id) => {
        SftGlobalData.post('reports&action=status', {
            key: apiRef.current,
            run_id: id,
        }).then(response => {
            const tx = tRef.current || ((k) => k);
            if (typeof response.error !== 'undefined' || !response?.data) {
                setErrorMessage((response && response.message) || tx('reports_error') || 'Error');
                setPhase('error');
                return;
            }
            const data = response.data;
            setStatusPayload(data);

            if (data.status === 'done') {
                setPhase('result');
                return;
            }
            if (data.status === 'error') {
                setErrorMessage(data.error || tx('reports_error') || 'Error');
                setPhase('error');
                return;
            }
            if (data.status === 'cancelled') {
                setPhase('vars');
                return;
            }

            // Still queued / running — back off after a while.
            pollAttempts.current += 1;
            const delay = pollAttempts.current < 10 ? 1000
                        : pollAttempts.current < 30 ? 2000
                        : 5000;
            schedulePoll(id, delay);
        });
    };

    const cancelRun = () => {
        if (!runId) {
            setPhase('vars');
            return;
        }
        if (pollTimer.current) {
            clearTimeout(pollTimer.current);
            pollTimer.current = null;
        }
        SftGlobalData.post('reports&action=cancel', {
            key: apiRef.current,
            run_id: runId,
        }).finally(() => {
            setPhase('vars');
            setRunId(null);
        });
    };

    // A finished run opens in the PDF view when the report has an .fr3
    // layout; an .frf-only layout (FastReport 2) stays grid with a note.
    const hasFr3Layout = !!report?.has_fr3_layout;
    const onlyFrfLayout = !!(report && Array.isArray(report.forms) && report.forms.length > 0 && !report.has_fr3_layout);
    useEffect(() => {
        if (phase !== 'result') return;
        setView(hasFr3Layout ? 'pdf' : 'grid');
        setViewNotice(onlyFrfLayout
            ? tl('reports_frf_grid', 'Deze layout is FastReport 2 (.frf) en is niet compatibel; de gegevens worden als grid getoond.')
            : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    // ─────────────────────────────────────────────────────────────────────────
    // Validation
    // ─────────────────────────────────────────────────────────────────────────

    const recIdProblem = validateRecIds(recIdSlots, recIdValues);

    const canSubmit = recIdProblem === '';

    // A changed value clears the "must select" message.
    useEffect(() => { setFilterError(''); }, [values]);

    // Run: the filter rule first (message like MFM), then - with "Opslaan in
    // mijn lijsten" ticked - the name, then the run.
    const onRun = () => {
        const problem = requiredFilterProblem(report, variables, values);
        if (problem) {
            setFilterError(problem.code === 'specific'
                ? tl('reports_mustSelectSpecificFilter', 'You must select filter') + ': ' + localizedVarLabel(problem.variable, props.lang)
                : tl('reports_mustSelectFilter', 'You must select a filter'));
            return;
        }
        if (saveToMy && props.isSft === true && !isMy) {
            setMyName(localizedTitle(report, props.lang));
            setNameDialog(true);
            return;
        }
        startExecution(null);
    };

    const confirmSaveName = () => {
        const name = myName.trim();
        if (name === '') return;
        setNameDialog(false);
        startExecution(null, { title: name });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render — variable input
    // ─────────────────────────────────────────────────────────────────────────

    const renderVariableInput = (v) => {
        const label = localizedVarLabel(v, props.lang) + (v.rep_must_use ? ' *' : '');
        const value = values[v.repvar];

        if (v.kind === 'date') {
            return (
                <DatePicker
                    key={v.repvar}
                    label={label}
                    value={value || null}
                    onChange={(newVal) => setValues(s => ({ ...s, [v.repvar]: newVal }))}
                    // Belgian notation (the app's LocalizationProvider has no
                    // locale, so the picker would default to MM/DD/YYYY).
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
            );
        }
        if (v.kind === 'number') {
            return (
                <TextField
                    key={v.repvar}
                    label={label}
                    type="number"
                    size="small"
                    fullWidth
                    value={value ?? ''}
                    onChange={(e) => setValues(s => ({ ...s, [v.repvar]: e.target.value }))}
                />
            );
        }
        if (v.kind === 'multi') {
            const options = optionsForVariable(v, optionLists);
            const textFn = (opt) => localizedCodeLabel(opt, props.lang);
            return (
                <MultiSelectField
                    key={v.repvar}
                    label={label}
                    options={options}
                    value={Array.isArray(value) ? value : []}
                    onChange={(newVal) => setValues(s => ({ ...s, [v.repvar]: newVal }))}
                    labelFn={(opt) => (textFn(opt) ? `${opt.value} — ${textFn(opt)}` : String(opt.value))}
                    tagLabelFn={textFn}
                    t={props.t}
                />
            );
        }
        // text / single
        return (
            <TextField
                key={v.repvar}
                label={label}
                size="small"
                fullWidth
                value={value ?? ''}
                onChange={(e) => setValues(s => ({ ...s, [v.repvar]: e.target.value }))}
            />
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render shells
    // ─────────────────────────────────────────────────────────────────────────

    const title = localizedTitle(report, props.lang);
    const isNarrow = (props.screenWidth ?? 1200) < 700;
    const dateVars = variables.filter(v => v.kind === 'date');

    // One document-id input: a pick list when the slot's column has one
    // (coid = firms), else a comma list (MyFashionManager multi-select).
    const renderRecIdInput = (slot) => (
        <RecIdInput
            key={'recid' + slot}
            report={report}
            slot={slot}
            lang={props.lang}
            lookups={props.lookups}
            required
            value={recIdValues[slot] || ''}
            onChange={(next) => setRecIdValues(s => ({ ...s, [slot]: next }))}
            helperText={tl('reports_recid_help', 'Meerdere: scheiden met een komma (12,13,14)')}
            t={props.t}
        />
    );

    const lookupList = (kind) => (props.lookups && Array.isArray(props.lookups[kind]) ? props.lookups[kind] : []);
    const renderScopeInput = (kind, value, setValue, label, maxLength) => {
        const all = lookupList(kind);
        const options = kind === 'registers' && storeId ? all.filter(o => !o.store || o.store === storeId) : all;
        if (options.length === 0) {
            return (
                <TextField label={label} size="small" fullWidth value={value}
                    inputProps={{ maxLength }}
                    onChange={(e) => setValue(e.target.value.toUpperCase())} />
            );
        }
        const current = options.find(o => o.value === value) || (value ? { value, label: '' } : null);
        return (
            <Autocomplete
                size="small"
                options={options}
                value={current}
                onChange={(_, nv) => setValue(nv ? nv.value : '')}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                // The readable name in the field; the code stays searchable and
                // muted in the list (the code is what the report uses).
                getOptionLabel={(opt) => (opt.label ? opt.label : String(opt.value))}
                filterOptions={(opts, { inputValue }) => {
                    const q = (inputValue || '').toLowerCase();
                    return q ? opts.filter(o => String(o.value).toLowerCase().includes(q)
                        || String(o.label || '').toLowerCase().includes(q)) : opts;
                }}
                renderOption={(optionProps, option) => (
                    <li {...optionProps} key={option.value}>
                        <Box component="span" sx={{ flex: 1 }}>{option.label || option.value}</Box>
                        {option.label && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {option.value}
                            </Typography>
                        )}
                    </li>
                )}
                renderInput={(params) => <TextField {...params} label={label} />}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' && phase === 'running') return;
                onClose && onClose();
            }}
            fullWidth
            maxWidth={phase === 'result' ? false : 'md'}
            PaperProps={{
                sx: {
                    m: isNarrow ? 1 : 4,
                    width: isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)',
                    ...(phase === 'result' ? { maxWidth: isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)' } : {}),
                    maxHeight: isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)',
                    height: phase === 'result' ? (isNarrow ? 'calc(100% - 16px)' : 'calc(100% - 64px)') : undefined,
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                <span>{title}</span>
                <IconButton size="small" onClick={onClose} disabled={phase === 'running'}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* ─── Variable input phase ─── */}
                {phase === 'vars' && (
                    <>
                        {loadingVars && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        )}
                        {needsContext && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {recIdSlots.length > 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        {tl('reports_recid_intro', 'This is a document report: enter the document id(s). A comma list (12,13,14) runs several documents at once.')}
                                    </Typography>
                                )}
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                    {recIdSlots.map(renderRecIdInput)}
                                    {report.uses_store && renderScopeInput('stores', storeId, setStoreId,
                                        tl('reports_store_label', 'Store (2 characters)'), 2)}
                                    {report.uses_pos && renderScopeInput('registers', posId, setPosId,
                                        tl('reports_pos_label', 'Register (4 characters)'), 4)}
                                </Box>
                                {recIdProblem === 'length' && (
                                    <Alert severity="warning">
                                        {tl('reports_recid_length', 'The id lists must have the same length (or a single value).')}
                                    </Alert>
                                )}
                            </Box>
                        )}
                        {!loadingVars && variables.length === 0 && !needsContext && (
                            <Typography variant="body2" color="text.secondary">
                                {props.t('reports_noVariables') || 'This report has no input variables. Click Run to execute.'}
                            </Typography>
                        )}
                        {!loadingVars && variables.length > 0 && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                {dateVars.length > 0 && (
                                    <DateQuickPicks
                                        range={dateVars.length >= 2}
                                        t={props.t}
                                        onPick={([start, end]) => setValues(s => (dateVars.length >= 2
                                            ? { ...s, [dateVars[0].repvar]: start, [dateVars[1].repvar]: end }
                                            : { ...s, [dateVars[0].repvar]: start }))}
                                    />
                                )}
                                {variables.map(renderVariableInput)}
                            </Box>
                        )}
                        {!loadingVars && report && report.must_select_filter && !filterError
                            && variables.some(v => v.kind === 'multi' || v.kind === 'text') && (
                            <Typography variant="body2" color="text.secondary">
                                {tl('reports_mustSelectFilterHint', 'Choose at least one filter for this report.')}
                            </Typography>
                        )}
                        {filterError && (
                            <Alert severity="warning">{filterError}</Alert>
                        )}
                        {/* Layout files (SoftTouch staff): edit / fetch / upload */}
                        {props.isSft === true && !isMy && report && Array.isArray(report.forms) && report.forms.length > 0 && (
                            <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.12)', pt: 1.5 }}>
                                <Typography variant="overline" color="text.secondary">
                                    {tl('reports_layouts', 'Layout files')}
                                </Typography>
                                <ReportLayoutActions
                                    api={props.api}
                                    sftUserId={props.sftUserId}
                                    repid={report.repid}
                                    t={props.t}
                                    lang={props.lang}
                                    theme={props.theme}
                                    notify={props.notify}
                                />
                            </Box>
                        )}
                    </>
                )}

                {/* ─── Running phase ─── */}
                {phase === 'running' && (
                    <>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <CircularProgress size={22} />
                            <Typography variant="body2">
                                {props.t('reports_running') || 'Running report…'}
                                {statusPayload && statusPayload.step && statusPayload.step_count
                                    ? ` (${statusPayload.step}/${statusPayload.step_count})`
                                    : ''}
                            </Typography>
                        </Stack>
                        <LinearProgress />
                        {[0, 1, 2, 3, 4, 5].map(i => (
                            <Skeleton key={i} variant="text" height={28} />
                        ))}
                    </>
                )}

                {/* ─── Result phase ─── */}
                {phase === 'result' && statusPayload && hasFr3Layout && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <ToggleButtonGroup size="small" exclusive value={view}
                            onChange={(_, next) => { if (next) { setView(next); setViewNotice(''); } }}>
                            <ToggleButton value="pdf" sx={{ textTransform: 'none', px: 1.5 }}>
                                <PictureAsPdfOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                                {tl('reports_view_pdf', 'PDF')}
                            </ToggleButton>
                            <ToggleButton value="grid" sx={{ textTransform: 'none', px: 1.5 }}>
                                <GridOnOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                                {tl('reports_view_grid', 'Grid')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                )}
                {phase === 'result' && viewNotice && (
                    <Alert severity="info">{viewNotice}</Alert>
                )}
                {phase === 'result' && statusPayload && hasFr3Layout && view === 'pdf' && runId && (
                    <ReportPdfView
                        api={props.api}
                        runId={runId}
                        t={props.t}
                        lang={props.lang}
                        title={title}
                        documentKind={String(report?.repmodule || '') === '2000' ? 'invoice' : 'ticket'}
                        storeId={storeId}
                        posId={posId}
                        variables={{ RepSelection: repSelection }}
                        onFallback={(msg) => {
                            setView('grid');
                            setViewNotice(tl('reports_pdf_fallback', 'De layout kon niet gerenderd worden; de gegevens worden als grid getoond.') + (msg ? ' (' + msg + ')' : ''));
                        }}
                    />
                )}
                {phase === 'result' && statusPayload && !(hasFr3Layout && view === 'pdf') && (
                    isSizeMatrix(statusPayload.columns || [])
                        ? (
                            <SizeMatrixReportGrid
                                columns={statusPayload.columns || []}
                                rows={statusPayload.rows || []}
                                truncated={!!statusPayload.truncated}
                                rowCount={statusPayload.row_count || 0}
                                layout={statusPayload.layout || null}
                                showSubtotals={/tussentota|subtota|sous-?tota/i.test(
                                    [report?.reptitle, report?.reptitle2, report?.reptitle3, report?.reptitle4].filter(Boolean).join(' ')
                                )}
                                t={props.t}
                                lang={props.lang}
                                theme={props.theme}
                                reportTitle={title}
                            />
                        )
                        : (
                            <ReportResultGrid
                                columns={statusPayload.columns || []}
                                rows={statusPayload.rows || []}
                                truncated={!!statusPayload.truncated}
                                rowCount={statusPayload.row_count || 0}
                                layout={statusPayload.layout || null}
                                t={props.t}
                                lang={props.lang}
                                theme={props.theme}
                                reportTitle={title}
                            />
                        )
                )}

                {/* ─── Error phase ─── */}
                {phase === 'error' && (
                    <Alert severity="error">
                        {errorMessage || (props.t('reports_error') || 'Error')}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                {phase === 'vars' && (
                    <>
                        {props.isSft === true && !isMy && (
                            <FormControlLabel
                                sx={{ mr: 'auto', ml: 0.5 }}
                                control={<Checkbox size="small" checked={saveToMy} onChange={(e) => setSaveToMy(e.target.checked)} />}
                                label={<Typography variant="body2">{tl('reports_saveToMy', 'Save to My lists')}</Typography>}
                            />
                        )}
                        <Button onClick={onClose}>{props.t('reports_close') || 'Close'}</Button>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            disabled={!canSubmit || loadingVars}
                            onClick={onRun}
                        >
                            {props.t('reports_run') || 'Run'}
                        </Button>
                    </>
                )}
                {phase === 'running' && (
                    <Button color="warning" onClick={cancelRun}>
                        {props.t('reports_cancel') || 'Cancel'}
                    </Button>
                )}
                {phase === 'result' && (
                    <>
                        <Button onClick={() => { setPhase('vars'); setStatusPayload(null); setRunId(null); }}>
                            {props.t('reports_runAgain') || 'Run again'}
                        </Button>
                        <Button variant="contained" onClick={onClose}>
                            {props.t('reports_close') || 'Close'}
                        </Button>
                    </>
                )}
                {phase === 'error' && (
                    <>
                        <Button onClick={() => setPhase('vars')}>
                            {props.t('reports_back') || 'Back'}
                        </Button>
                        <Button variant="contained" onClick={onClose}>
                            {props.t('reports_close') || 'Close'}
                        </Button>
                    </>
                )}
            </DialogActions>

            {/* The name of the copy in "Mijn lijsten" (the title proposed). */}
            <Dialog open={nameDialog} onClose={() => setNameDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{tl('reports_saveToMy', 'Save to My lists')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        sx={{ mt: 1 }}
                        label={tl('reports_my_name', 'Name')}
                        value={myName}
                        inputProps={{ maxLength: 150 }}
                        onChange={(e) => setMyName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmSaveName(); }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNameDialog(false)}>{tl('reports_cancel', 'Cancel')}</Button>
                    <Button variant="contained" startIcon={<PlayArrowIcon />} disabled={myName.trim() === ''} onClick={confirmSaveName}>
                        {tl('reports_my_saveAndRun', 'Save and run')}
                    </Button>
                </DialogActions>
            </Dialog>

        </Dialog>
    );
};

export default ReportRunModal;
